import React, { Component } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import configData from "../../config.json";
import DataSourceManager from "../common/DataSourceManager";
import InteractionEngine from "../common/InteractionEngine";
import GroupedFormRenderer from "../common/GroupedFormRenderer";

/**
 * DynamicFormAdapter - Standalone form adapter with data source + interaction support.
 * Renders forms from formConfig.fields[] with API-backed dropdowns via DataSourceManager
 * and dynamic field behaviors (cascade, visibility, computed, auto-fill, enable/disable)
 * via InteractionEngine.
 */
export default class DynamicFormAdapter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formValues: {},
      errors: {},
      submitting: false,
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      // DataSource state
      dataSourceOptions: {}, // { fieldName: [{ value, label }] }
      dataSourcesLoaded: false,
      // Interaction state
      fieldVisibility: {},   // { fieldName: true/false }
      fieldDisabled: {},     // { fieldName: true/false }
      fieldOptions: {},      // { fieldName: [{ value, label }] } — overrides from InteractionEngine
    };
    this.dataSourceManager = null;
    this.dsUnsubscribers = [];
    this.interactionEngine = null;
  }

  componentDidMount() {
    const { config } = this.props;
    if (!config) return;
    const formConfig = config.formConfig || {};
    const fields = this._getAllFields(formConfig);

    // Set default values
    const defaults = {};
    fields.forEach((f) => {
      defaults[f.fieldName] = f.defaultValue || "";
    });
    this.setState({ formValues: defaults });

    // Initialize DataSourceManager if dataSources are defined
    const dataSources = formConfig.dataSources || [];
    if (dataSources.length > 0) {
      this.dataSourceManager = new DataSourceManager(
        dataSources,
        this.state.sId,
        this.state.uId
      );

      // Subscribe to each data source for fields that reference them
      fields.forEach((field) => {
        if (field.dataSource) {
          const unsub = this.dataSourceManager.subscribe(field.dataSource, (options) => {
            this.setState((prev) => ({
              dataSourceOptions: {
                ...prev.dataSourceOptions,
                [field.fieldName]: options,
              },
            }));
          });
          this.dsUnsubscribers.push(unsub);
        }
      });

      // Load all non-dependent sources
      this.dataSourceManager.loadInitialSources().then((optionsMap) => {
        const dsOptions = {};
        fields.forEach((field) => {
          if (field.dataSource && optionsMap[field.dataSource]) {
            dsOptions[field.fieldName] = optionsMap[field.dataSource];
          }
        });
        this.setState({ dataSourceOptions: dsOptions, dataSourcesLoaded: true });
      });
    } else {
      this.setState({ dataSourcesLoaded: true });
    }

    // Initialize InteractionEngine if interactions are defined
    const interactions = formConfig.interactions || [];
    if (interactions.length > 0) {
      this.interactionEngine = new InteractionEngine(
        interactions,
        {
          setFieldValue: (fieldName, value) => {
            this.setState((prev) => ({
              formValues: { ...prev.formValues, [fieldName]: value },
            }));
          },
          setFieldOptions: (fieldName, options) => {
            this.setState((prev) => ({
              fieldOptions: { ...prev.fieldOptions, [fieldName]: options },
            }));
          },
          setFieldVisibility: (fieldName, visible) => {
            this.setState((prev) => ({
              fieldVisibility: { ...prev.fieldVisibility, [fieldName]: visible },
            }));
          },
          setFieldDisabled: (fieldName, disabled) => {
            this.setState((prev) => ({
              fieldDisabled: { ...prev.fieldDisabled, [fieldName]: disabled },
            }));
          },
          getFieldValue: (fieldName) => {
            return this.state.formValues[fieldName];
          },
          dataSourceManager: this.dataSourceManager,
        },
        this.state.sId,
        this.state.uId
      );

      // Evaluate visibility/enable defaults for initial values
      const visibilityDefaults = this.interactionEngine.getVisibilityDefaults(defaults);
      const disabledDefaults = this.interactionEngine.getDisabledDefaults(defaults);
      this.setState({ fieldVisibility: visibilityDefaults, fieldDisabled: disabledDefaults });
    }
  }

  componentWillUnmount() {
    // Clean up subscriptions
    this.dsUnsubscribers.forEach((unsub) => unsub());
    if (this.dataSourceManager) {
      this.dataSourceManager.destroy();
    }
    if (this.interactionEngine) {
      this.interactionEngine.destroy();
    }
  }

  /**
   * Get all fields from formConfig (supports both flat and fieldGroups format)
   */
  _getAllFields(formConfig) {
    if (formConfig.fieldGroups && formConfig.fieldGroups.length > 0) {
      return formConfig.fieldGroups.flatMap((g) => g.fields || []);
    }
    return formConfig.fields || [];
  }

  handleFieldChange = (fieldName, value) => {
    this.setState(
      (prev) => ({
        formValues: { ...prev.formValues, [fieldName]: value },
      }),
      () => {
        // Check if any data sources depend on this field and refresh them
        this._handleDependentSources(fieldName, value);

        // Process interactions triggered by this field change
        if (this.interactionEngine) {
          this.interactionEngine.onFieldChange(fieldName, value, this.state.formValues);
        }
      }
    );
  };

  /**
   * When a field changes, check if any data sources depend on it and refresh
   */
  _handleDependentSources = (fieldName, value) => {
    if (!this.dataSourceManager) return;

    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const dataSources = formConfig.dataSources || [];
    const fields = this._getAllFields(formConfig);

    // Find data sources that depend on this field
    dataSources.forEach((ds) => {
      if (ds.dependsOn === fieldName) {
        // Find the field that uses this data source
        const dependentField = fields.find((f) => f.dataSource === ds.id);
        if (dependentField) {
          // Clear the dependent field's value
          this.setState((prev) => ({
            formValues: { ...prev.formValues, [dependentField.fieldName]: "" },
          }));

          if (value) {
            // Fetch new options with parent value
            const paramField = ds.dependsOnParam || ds.dependsOn;
            this.dataSourceManager.fetchOptions(ds.id, { [paramField]: value });
          } else {
            // Clear options if parent is cleared
            this.setState((prev) => ({
              dataSourceOptions: { ...prev.dataSourceOptions, [dependentField.fieldName]: [] },
            }));
          }
        }
      }
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { config } = this.props;
    const actionsConfig = config.actionsConfig || {};
    const endpoint = actionsConfig.addEndpoint || config.apiEndPoint;
    if (!endpoint) return;

    this.setState({ submitting: true });
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    // Only submit visible fields
    const { formValues, fieldVisibility } = this.state;
    Object.entries(formValues).forEach(([key, value]) => {
      // Skip fields that are hidden by interaction rules
      if (fieldVisibility[key] === false) return;
      formData.append(key, value);
    });

    fetch(configData.api_url + endpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          toast.success(data.message);
          if (this.props.onEvent) {
            this.props.onEvent("form_submitted", config.widget_instances_Id, this.state.formValues);
          }
        } else {
          toast.error(data.message);
        }
        this.setState({ submitting: false });
      })
      .catch(() => {
        toast.error("Failed to submit form");
        this.setState({ submitting: false });
      });
  };

  /**
   * Render a single form field based on its inputType and dataSource
   */
  renderField = (field) => {
    const value = this.state.formValues[field.fieldName] || "";
    const inputType = (field.inputType || "text").toLowerCase();
    const hasDataSource = !!field.dataSource;
    const dsOptions = this.state.dataSourceOptions[field.fieldName] || [];

    // InteractionEngine may override options for this field (cascade)
    const interactionOptions = this.state.fieldOptions[field.fieldName];
    const isDisabledByInteraction = this.state.fieldDisabled[field.fieldName] || false;

    // API-backed select: render Form.Select with fetched options
    if (hasDataSource || inputType === "select") {
      // Priority: interactionOptions > dsOptions > static options
      const options = interactionOptions || (hasDataSource ? dsOptions : (field.options || []));
      const isLoading = hasDataSource && this.dataSourceManager && this.dataSourceManager.isLoading(field.dataSource);

      return (
        <Form.Select
          value={value}
          onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
          required={field.required}
          disabled={isLoading || isDisabledByInteraction}
        >
          <option value="">
            {isLoading ? "Loading..." : `Select ${field.displayName || field.fieldName}`}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    // Other input types
    switch (inputType) {
      case "number":
        return (
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabledByInteraction}
          />
        );
      case "date":
        return (
          <Form.Control
            type="date"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            disabled={isDisabledByInteraction}
          />
        );
      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={field.rows || 3}
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabledByInteraction}
          />
        );
      case "email":
        return (
          <Form.Control
            type="email"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabledByInteraction}
          />
        );
      case "tel":
        return (
          <Form.Control
            type="tel"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabledByInteraction}
          />
        );
      case "multiselect":
        return (
          <Form.Select
            multiple
            value={value ? (Array.isArray(value) ? value : [value]) : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
              this.handleFieldChange(field.fieldName, selected);
            }}
            required={field.required}
            disabled={isDisabledByInteraction}
          >
            {(interactionOptions || (hasDataSource ? dsOptions : (field.options || []))).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        );
      default:
        return (
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isDisabledByInteraction}
          />
        );
    }
  };

  /**
   * Render flat fields (backward compatible, single group or no groups)
   */
  renderFlatFields = (fields) => {
    const { fieldVisibility } = this.state;
    return fields.map((field) => {
      if (fieldVisibility[field.fieldName] === false) {
        return null;
      }
      return (
        <Row key={field.fieldName} className="mb-2">
          <Col lg="4" md="4">
            <Form.Label>
              {field.displayName || field.fieldName}
              {field.required && <span className="text-danger">*</span>}
            </Form.Label>
          </Col>
          <Col lg="8" md="8">
            {this.renderField(field)}
            {this.state.errors[field.fieldName] && (
              <div className="text-danger" style={{ fontSize: "0.875rem" }}>
                {this.state.errors[field.fieldName]}
              </div>
            )}
          </Col>
        </Row>
      );
    });
  };

  render() {
    const { config } = this.props;
    if (!config) {
      return <div className="dynamic-form-widget p-3 text-muted">No form configuration provided.</div>;
    }
    const formConfig = config.formConfig || {};
    const hasMultipleGroups = formConfig.fieldGroups && formConfig.fieldGroups.length > 1;

    return (
      <div className="dynamic-form-widget p-3">
        <Form onSubmit={this.handleSubmit}>
          {hasMultipleGroups ? (
            <GroupedFormRenderer
              fieldGroups={formConfig.fieldGroups}
              formValues={this.state.formValues}
              errors={this.state.errors}
              onFieldChange={this.handleFieldChange}
              fieldVisibility={this.state.fieldVisibility}
              fieldDisabled={this.state.fieldDisabled}
              fieldOptions={this.state.fieldOptions}
              dataSourceOptions={this.state.dataSourceOptions}
              dataSourceManager={this.dataSourceManager}
              renderMode={formConfig.groupRenderMode || "accordion"}
            />
          ) : (
            this.renderFlatFields(this._getAllFields(formConfig))
          )}
          <Row className="mt-3">
            <Col className="d-flex justify-content-end">
              <Button type="submit" variant="success" disabled={this.state.submitting}>
                {this.state.submitting ? "Submitting..." : "Submit"}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }
}
